import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";
import type { MaybeRefOrGetter } from "vue";

import { computed, toValue } from "vue";

import {
  applyPendingSiteSectionPreview,
  type TPendingSiteSectionPreview,
} from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree-pending-preview";

type TreeNode = IVirtualizedTreeNode<TSiteSectionsTreeData>;

export interface IPendingSiteSectionCreationPreview {
  isVisible: boolean;
  name: string;
  parent: TSiteSectionsTreeData;
}

export interface IPendingSiteSectionMovePreview {
  section: SectionNode;
  parent: TSiteSectionsTreeData;
}

interface IUseSiteSectionsTreePendingPreviewOptions {
  siteSectionsTree: MaybeRefOrGetter<TreeNode | undefined>;
  filteredSiteSectionsTree: MaybeRefOrGetter<TreeNode | undefined>;
  creationPreview: MaybeRefOrGetter<IPendingSiteSectionCreationPreview>;
  movePreview: MaybeRefOrGetter<IPendingSiteSectionMovePreview | null>;
}

export function useSiteSectionsTreePendingPreview(options: IUseSiteSectionsTreePendingPreviewOptions) {
  const pendingPreview = computed<TPendingSiteSectionPreview | null>(() => {
    const sourceTree = toValue(options.siteSectionsTree);
    const creationPreview = toValue(options.creationPreview);

    if (creationPreview.isVisible) {
      return {
        type: "creation",
        name: creationPreview.name,
        parent: creationPreview.parent,
        sourceTree,
      };
    }

    const movePreview = toValue(options.movePreview);
    if (!movePreview) return null;

    return {
      type: "move",
      name: movePreview.section.name,
      section: movePreview.section,
      parent: movePreview.parent,
      sourceTree,
    };
  });

  const siteSectionsTreeWithPendingPreview = computed<TreeNode | undefined>(() => {
    return applyPendingSiteSectionPreview(toValue(options.filteredSiteSectionsTree), pendingPreview.value);
  });

  return {
    pendingPreview,
    siteSectionsTreeWithPendingPreview,
  };
}
